import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { chaptersApi, scenesApi, charactersApi, locationsApi, plotNotesApi, timelineApi, booksApi, writingApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Progress } from '../components/ui/progress';
import {
    PenLine, ChevronLeft, Plus, FileText, Users, MapPin, Clock,
    Sparkles, Trash2, GripVertical, BookOpen, MoreVertical,
    Wand2, StickyNote, Flame, Image, AlignJustify,
    LayoutGrid, Maximize2, SplitSquareHorizontal, History, Type, Search
} from 'lucide-react';
import { toast } from 'sonner';
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import {
    DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors
} from '@dnd-kit/core';
import {
    arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Import editor panel components
import PlotGrid from '../components/editor/PlotGrid';
import CharactersPanel from '../components/editor/CharactersPanel';
import WorldbuildingPanel from '../components/editor/WorldbuildingPanel';
import TimelinePanel from '../components/editor/TimelinePanel';
import NotesPanel from '../components/editor/NotesPanel';
import AIPanel from '../components/editor/AIPanel';
import IllustrationsPanel from '../components/editor/IllustrationsPanel';
import ExportButton from '../components/editor/ExportButton';
import WritingAIAssistant from '../components/editor/WritingAIAssistant';

// Import new professional writing features
import CorkboardView from '../components/editor/CorkboardView';
import CompositionMode from '../components/editor/CompositionMode';
import SplitScreenView from '../components/editor/SplitScreenView';
import ProBookFormatting from '../components/editor/ProBookFormatting';
import FindReplace from '../components/editor/FindReplace';
import { SnapshotButton } from '../components/editor/SnapshotManager';

// Import collaboration features
import useCollaboration from '../hooks/useCollaboration';
import CollaboratorAvatars from '../components/editor/CollaboratorAvatars';

const BookEditor = ({ book, onBack, onNavigate }) => {
    const { user, refreshUser } = useAuth();
    const [activeTab, setActiveTab] = useState('write');
    const [chapters, setChapters] = useState([]);
    const [selectedChapter, setSelectedChapter] = useState(null);
    const [scenes, setScenes] = useState([]);
    const [selectedScene, setSelectedScene] = useState(null);
    const [characters, setCharacters] = useState([]);
    const [locations, setLocations] = useState([]);
    const [plotNotes, setPlotNotes] = useState([]);
    const [timeline, setTimeline] = useState([]);
    const [stats, setStats] = useState(null);
    const [showAIPanel, setShowAIPanel] = useState(false);
    const [editorContent, setEditorContent] = useState('');
    const [saving, setSaving] = useState(false);
    const [streak, setStreak] = useState(null);
    const [lineSpacing, setLineSpacing] = useState(() => {
        return localStorage.getItem('eskrybe-line-spacing') || 'normal';
    });
    
    // New view states for professional features
    const [showCompositionMode, setShowCompositionMode] = useState(false);
    const [showSplitScreen, setShowSplitScreen] = useState(false);
    const [showFindReplace, setShowFindReplace] = useState(false);
    
    const saveTimeoutRef = useRef(null);
    const previousWordCountRef = useRef(0);
    const collaborationEnabledRef = useRef(false);

    // Determine current document for collaboration
    const currentDocType = selectedScene ? 'scene' : 'chapter';
    const currentDocId = selectedScene?.id || selectedChapter?.id;
    
    // Real-time collaboration hook - enabled for premium users
    const isPremiumUser = user?.subscription_tier && ['author', 'studio'].includes(user.subscription_tier);
    const {
        collaborators,
        isConnected: isCollabConnected,
        myColor,
        sendEdit,
        sendCursor,
        onContentUpdate
    } = useCollaboration(
        currentDocType,
        currentDocId,
        user?.id,
        user?.name || user?.email,
        isPremiumUser && !!currentDocId
    );

    // Handle incoming collaboration edits
    useEffect(() => {
        if (!isPremiumUser) return;
        
        onContentUpdate((content, source, meta) => {
            if (source === 'edit' && meta?.userId !== user?.id) {
                // Another user made an edit - update content without triggering our own broadcast
                collaborationEnabledRef.current = false;
                setEditorContent(content);
                setTimeout(() => {
                    collaborationEnabledRef.current = true;
                }, 100);
            }
        });
    }, [isPremiumUser, user?.id, onContentUpdate]);

    // Save line spacing preference
    useEffect(() => {
        localStorage.setItem('eskrybe-line-spacing', lineSpacing);
    }, [lineSpacing]);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    useEffect(() => {
        loadBookData();
        loadStreak();
    }, [book.id]);

    const loadStreak = async () => {
        try {
            const res = await writingApi.getStreak();
            setStreak(res.data);
        } catch (error) {
            console.error('Failed to load streak');
        }
    };

    const loadBookData = async () => {
        try {
            const [chaptersRes, charsRes, locsRes, notesRes, timelineRes, statsRes] = await Promise.all([
                chaptersApi.list(book.id),
                charactersApi.list(book.id),
                locationsApi.list(book.id),
                plotNotesApi.list(book.id),
                timelineApi.list(book.id),
                booksApi.stats(book.id),
            ]);
            setChapters(chaptersRes.data);
            setCharacters(charsRes.data);
            setLocations(locsRes.data);
            setPlotNotes(notesRes.data);
            setTimeline(timelineRes.data);
            setStats(statsRes.data);

            if (chaptersRes.data.length > 0) {
                setSelectedChapter(chaptersRes.data[0]);
                const scenesRes = await scenesApi.list(chaptersRes.data[0].id);
                setScenes(scenesRes.data);
            }
        } catch (error) {
            toast.error('Failed to load book data');
        }
    };

    const handleChapterSelect = async (chapter) => {
        setSelectedChapter(chapter);
        setSelectedScene(null);
        setEditorContent(chapter.content || '');
        // Track initial word count for streak calculation
        const wordCount = (chapter.content || '').replace(/<[^>]*>/g, '').split(/\s+/).filter(w => w.length > 0).length;
        previousWordCountRef.current = wordCount;
        try {
            const scenesRes = await scenesApi.list(chapter.id);
            setScenes(scenesRes.data);
        } catch (error) {
            toast.error('Failed to load scenes');
        }
    };

    const handleSceneSelect = (scene) => {
        setSelectedScene(scene);
        setEditorContent(scene.content || '');
        // Track initial word count for streak calculation
        const wordCount = (scene.content || '').replace(/<[^>]*>/g, '').split(/\s+/).filter(w => w.length > 0).length;
        previousWordCountRef.current = wordCount;
    };

    const handleContentChange = (content) => {
        setEditorContent(content);
        
        // Broadcast to collaborators if enabled
        if (isPremiumUser && collaborationEnabledRef.current) {
            sendEdit(content);
        }
        
        if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = setTimeout(() => autoSave(content), 2000);
    };

    // Enable collaboration after initial load
    useEffect(() => {
        if (selectedChapter || selectedScene) {
            setTimeout(() => {
                collaborationEnabledRef.current = true;
            }, 500);
        }
        return () => {
            collaborationEnabledRef.current = false;
        };
    }, [selectedChapter?.id, selectedScene?.id]);

    const autoSave = async (content) => {
        setSaving(true);
        try {
            // Calculate words written in this session
            const currentWordCount = content.replace(/<[^>]*>/g, '').split(/\s+/).filter(w => w.length > 0).length;
            const wordsWritten = Math.max(0, currentWordCount - previousWordCountRef.current);
            
            if (selectedScene) {
                await scenesApi.update(selectedScene.id, { content });
                setSelectedScene({ ...selectedScene, content });
            } else if (selectedChapter) {
                await chaptersApi.update(selectedChapter.id, { content });
                setSelectedChapter({ ...selectedChapter, content });
            }
            
            // Log writing session if new words were written
            if (wordsWritten > 0) {
                try {
                    const streakRes = await writingApi.logSession({
                        book_id: book.id,
                        words_written: wordsWritten,
                        duration_minutes: 0
                    });
                    setStreak(streakRes.data.streak);
                } catch (e) {
                    console.error('Failed to log writing session');
                }
                previousWordCountRef.current = currentWordCount;
            }
            
            const statsRes = await booksApi.stats(book.id);
            setStats(statsRes.data);
        } catch (error) {
            console.error('Auto-save failed:', error);
        } finally {
            setSaving(false);
        }
    };

    const handleDragEnd = async (event) => {
        const { active, over } = event;
        if (active.id !== over.id) {
            const oldIndex = chapters.findIndex(c => c.id === active.id);
            const newIndex = chapters.findIndex(c => c.id === over.id);
            const newChapters = arrayMove(chapters, oldIndex, newIndex);
            setChapters(newChapters);
            
            // Update order in backend
            for (let i = 0; i < newChapters.length; i++) {
                await chaptersApi.update(newChapters[i].id, { order: i });
            }
        }
    };

    const quillModules = {
        toolbar: [
            [{ 'header': [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            ['blockquote'],
            ['clean']
        ],
    };

    return (
        <div className="min-h-screen bg-background flex">
            {/* Left Sidebar - Chapters & Scenes */}
            <aside className="w-56 border-r border-border bg-muted/30 flex flex-col shrink-0">
                <div className="p-4 border-b border-border">
                    <Button variant="ghost" onClick={onBack} className="mb-3 -ml-2" data-testid="back-to-dashboard">
                        <ChevronLeft className="h-4 w-4 mr-1" />
                        Dashboard
                    </Button>
                    <h2 className="font-serif text-lg font-semibold line-clamp-1">{book.title}</h2>
                    {stats && (
                        <div className="mt-2">
                            <div className="flex justify-between text-xs text-muted-foreground mb-1">
                                <span>{stats.total_words.toLocaleString()} / {stats.target_words.toLocaleString()}</span>
                                <span>{stats.progress_percent}%</span>
                            </div>
                            <Progress value={stats.progress_percent} className="h-1" />
                        </div>
                    )}
                </div>

                <div className="flex-1 overflow-hidden">
                    <Tabs defaultValue="chapters" className="h-full flex flex-col">
                        <TabsList className="mx-4 mt-4 grid grid-cols-2">
                            <TabsTrigger value="chapters">Chapters</TabsTrigger>
                            <TabsTrigger value="scenes">Scenes</TabsTrigger>
                        </TabsList>

                        <TabsContent value="chapters" className="flex-1 overflow-hidden mt-2">
                            <ScrollArea className="h-full">
                                <div className="p-4 space-y-2">
                                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                                        <SortableContext items={chapters.map(c => c.id)} strategy={verticalListSortingStrategy}>
                                            {chapters.map((chapter) => (
                                                <SortableChapterItem
                                                    key={chapter.id}
                                                    chapter={chapter}
                                                    isSelected={selectedChapter?.id === chapter.id}
                                                    onClick={() => handleChapterSelect(chapter)}
                                                    onDelete={async () => {
                                                        await chaptersApi.delete(chapter.id);
                                                        setChapters(chapters.filter(c => c.id !== chapter.id));
                                                        if (selectedChapter?.id === chapter.id) {
                                                            setSelectedChapter(null);
                                                            setScenes([]);
                                                        }
                                                        toast.success('Chapter deleted');
                                                    }}
                                                />
                                            ))}
                                        </SortableContext>
                                    </DndContext>
                                    <NewChapterButton bookId={book.id} chapters={chapters} setChapters={setChapters} />
                                </div>
                            </ScrollArea>
                        </TabsContent>

                        <TabsContent value="scenes" className="flex-1 overflow-hidden mt-2">
                            <ScrollArea className="h-full">
                                <div className="p-4 space-y-2">
                                    {!selectedChapter ? (
                                        <p className="text-sm text-muted-foreground text-center py-4">
                                            Select a chapter first
                                        </p>
                                    ) : (
                                        <>
                                            {scenes.map((scene) => (
                                                <div
                                                    key={scene.id}
                                                    className={`p-3 rounded-lg cursor-pointer transition-colors ${
                                                        selectedScene?.id === scene.id 
                                                            ? 'bg-primary text-primary-foreground' 
                                                            : 'bg-card hover:bg-muted'
                                                    }`}
                                                    onClick={() => handleSceneSelect(scene)}
                                                    data-testid={`scene-${scene.id}`}
                                                >
                                                    <div className="font-medium text-sm">{scene.title}</div>
                                                    <div className="text-xs opacity-70 mt-1">
                                                        {scene.word_count || 0} words
                                                    </div>
                                                </div>
                                            ))}
                                            <NewSceneButton 
                                                chapterId={selectedChapter.id} 
                                                scenes={scenes} 
                                                setScenes={setScenes} 
                                            />
                                        </>
                                    )}
                                </div>
                            </ScrollArea>
                        </TabsContent>
                    </Tabs>
                </div>
            </aside>

            {/* Main Editor Area */}
            <main className="flex-1 flex flex-col">
                {/* Editor Header */}
                <header className="h-14 border-b border-border flex items-center justify-between px-6">
                    <div className="flex items-center gap-4">
                        <Tabs value={activeTab} onValueChange={setActiveTab}>
                            <TabsList>
                                <TabsTrigger value="write" data-testid="tab-write">
                                    <PenLine className="h-4 w-4 mr-2" />
                                    Write
                                </TabsTrigger>
                                <TabsTrigger value="corkboard" data-testid="tab-corkboard">
                                    <LayoutGrid className="h-4 w-4 mr-2" />
                                    Corkboard
                                </TabsTrigger>
                                <TabsTrigger value="plot" data-testid="tab-plot">
                                    <FileText className="h-4 w-4 mr-2" />
                                    Plot
                                </TabsTrigger>
                                <TabsTrigger value="characters" data-testid="tab-characters">
                                    <Users className="h-4 w-4 mr-2" />
                                    Characters
                                </TabsTrigger>
                                <TabsTrigger value="world" data-testid="tab-world">
                                    <MapPin className="h-4 w-4 mr-2" />
                                    World
                                </TabsTrigger>
                                <TabsTrigger value="timeline" data-testid="tab-timeline">
                                    <Clock className="h-4 w-4 mr-2" />
                                    Timeline
                                </TabsTrigger>
                                <TabsTrigger value="notes" data-testid="tab-notes">
                                    <StickyNote className="h-4 w-4 mr-2" />
                                    Notes
                                </TabsTrigger>
                                <TabsTrigger value="illustrations" data-testid="tab-illustrations">
                                    <Image className="h-4 w-4 mr-2" />
                                    Illustrations
                                </TabsTrigger>
                                <TabsTrigger value="formatting" data-testid="tab-formatting">
                                    <Type className="h-4 w-4 mr-2" />
                                    Formatting
                                </TabsTrigger>
                            </TabsList>
                        </Tabs>
                    </div>

                    <div className="flex items-center gap-3">
                        {saving && <span className="text-xs text-muted-foreground">Saving...</span>}
                        
                        {/* Real-time Collaboration Indicator */}
                        {isPremiumUser && currentDocId && (
                            <CollaboratorAvatars
                                collaborators={collaborators}
                                isConnected={isCollabConnected}
                                myColor={myColor}
                            />
                        )}
                        
                        {streak && streak.current_streak > 0 && (
                            <div className="flex items-center gap-1 text-sm px-2 py-1 bg-accent/10 rounded-full" title={`${streak.words_today} words today`}>
                                <Flame className="h-4 w-4 text-accent" />
                                <span className="font-medium">{streak.current_streak}</span>
                            </div>
                        )}
                        <div className="flex items-center gap-2 text-sm">
                            <Sparkles className="h-4 w-4 text-accent" />
                            <span>{user?.credits || 0}</span>
                        </div>
                        
                        {/* View Mode Buttons */}
                        {(selectedChapter || selectedScene) && activeTab === 'write' && (
                            <div className="flex items-center gap-1 border-l pl-3 ml-2">
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setShowFindReplace(true)}
                                    title="Find & Replace"
                                    data-testid="find-replace-btn"
                                >
                                    <Search className="h-4 w-4" />
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setShowSplitScreen(true)}
                                    title="Split Screen View"
                                    data-testid="split-screen-btn"
                                >
                                    <SplitSquareHorizontal className="h-4 w-4" />
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setShowCompositionMode(true)}
                                    title="Focus Mode"
                                    data-testid="composition-mode-btn"
                                >
                                    <Maximize2 className="h-4 w-4" />
                                </Button>
                                {selectedChapter && (
                                    <SnapshotButton
                                        bookId={book.id}
                                        chapter={selectedChapter}
                                        currentContent={editorContent}
                                        onRestore={(content) => {
                                            setEditorContent(content);
                                            toast.success('Snapshot restored');
                                        }}
                                    />
                                )}
                            </div>
                        )}
                        
                        <Button 
                            variant={showAIPanel ? "default" : "outline"}
                            onClick={() => setShowAIPanel(!showAIPanel)}
                            data-testid="toggle-ai-panel"
                        >
                            <Wand2 className="h-4 w-4 mr-2" />
                            AI Assist
                        </Button>
                        <ExportButton book={book} chapters={chapters} characters={characters} />
                    </div>
                </header>

                {/* Content Area */}
                <div className="flex-1 flex overflow-hidden">
                    <div className="flex-1 overflow-auto">
                        {activeTab === 'write' && (
                            <div className="max-w-4xl mx-auto px-6 py-6">
                                {selectedChapter || selectedScene ? (
                                    <>
                                        <div className="flex items-center justify-between mb-6">
                                            <h1 className="font-serif text-2xl font-semibold">
                                                {selectedScene?.title || selectedChapter?.title}
                                            </h1>
                                            {/* Line Spacing Control */}
                                            <div className="flex items-center gap-2">
                                                <AlignJustify className="h-4 w-4 text-muted-foreground" />
                                                <select
                                                    value={lineSpacing}
                                                    onChange={(e) => setLineSpacing(e.target.value)}
                                                    className="h-8 text-xs rounded-md border border-input bg-background px-2"
                                                    data-testid="line-spacing-select"
                                                >
                                                    <option value="tight">Tight (1.3)</option>
                                                    <option value="normal">Normal (1.5)</option>
                                                    <option value="relaxed">Relaxed (1.8)</option>
                                                    <option value="double">Double (2.0)</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div className={`line-spacing-${lineSpacing}`}>
                                            <ReactQuill
                                                theme="snow"
                                                value={editorContent}
                                                onChange={handleContentChange}
                                                modules={quillModules}
                                                className="editor-content"
                                                data-testid="editor"
                                            />
                                        </div>
                                        
                                        {/* Inline AI Writing Assistant */}
                                        <WritingAIAssistant
                                            bookId={book.id}
                                            chapterTitle={selectedScene?.title || selectedChapter?.title}
                                            currentContent={editorContent}
                                            characters={characters}
                                            onInsertText={(text) => {
                                                setEditorContent(prev => prev + '\n\n' + text);
                                            }}
                                        />
                                    </>
                                ) : (
                                    <div className="text-center py-20">
                                        <BookOpen className="h-16 w-16 mx-auto text-muted-foreground/50 mb-4" />
                                        <h2 className="font-serif text-xl font-semibold mb-2">
                                            Select a chapter to start writing
                                        </h2>
                                        <p className="text-muted-foreground">
                                            Or create your first chapter from the sidebar
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}

                        {activeTab === 'plot' && (
                            <PlotGrid 
                                book={book}
                                chapters={chapters} 
                                scenes={scenes}
                                plotNotes={plotNotes}
                                onSelectChapter={handleChapterSelect}
                            />
                        )}

                        {activeTab === 'characters' && (
                            <CharactersPanel 
                                bookId={book.id}
                                characters={characters}
                                setCharacters={setCharacters}
                            />
                        )}

                        {activeTab === 'world' && (
                            <WorldbuildingPanel 
                                bookId={book.id}
                                locations={locations}
                                setLocations={setLocations}
                            />
                        )}

                        {activeTab === 'timeline' && (
                            <TimelinePanel 
                                bookId={book.id}
                                events={timeline}
                                setEvents={setTimeline}
                                characters={characters}
                                locations={locations}
                            />
                        )}

                        {activeTab === 'notes' && (
                            <NotesPanel 
                                bookId={book.id}
                                notes={plotNotes}
                                setNotes={setPlotNotes}
                            />
                        )}

                        {activeTab === 'illustrations' && (
                            <IllustrationsPanel 
                                bookId={book.id}
                                characters={characters}
                            />
                        )}

                        {activeTab === 'corkboard' && (
                            <CorkboardView
                                bookId={book.id}
                                chapters={chapters}
                                setChapters={setChapters}
                                scenes={scenes}
                                setScenes={setScenes}
                                onSelectChapter={handleChapterSelect}
                                onSelectScene={handleSceneSelect}
                            />
                        )}

                        {activeTab === 'formatting' && (
                            <ProBookFormatting
                                bookId={book.id}
                                chapters={chapters}
                            />
                        )}
                    </div>

                    {/* AI Panel */}
                    {showAIPanel && (
                        <AIPanel 
                            onClose={() => setShowAIPanel(false)}
                            context={editorContent}
                            onInsert={(text) => {
                                setEditorContent(editorContent + '\n\n' + text);
                            }}
                            refreshUser={refreshUser}
                            onNavigate={onNavigate}
                        />
                    )}
                </div>
            </main>

            {/* Composition Mode Modal */}
            <CompositionMode
                isOpen={showCompositionMode}
                onClose={() => setShowCompositionMode(false)}
                content={editorContent}
                onContentChange={handleContentChange}
                chapterTitle={selectedScene?.title || selectedChapter?.title}
                wordCount={editorContent?.replace(/<[^>]*>/g, '').split(/\s+/).filter(Boolean).length || 0}
                totalWordCount={stats?.total_words || 0}
                onSave={() => autoSave(editorContent)}
                chapters={chapters}
                currentChapterIndex={chapters.findIndex(c => c.id === selectedChapter?.id)}
                onNavigateChapter={(idx) => {
                    if (chapters[idx]) {
                        handleChapterSelect(chapters[idx]);
                    }
                }}
            />

            {/* Split Screen View Modal */}
            <SplitScreenView
                isOpen={showSplitScreen}
                onClose={() => setShowSplitScreen(false)}
                editorContent={editorContent}
                onContentChange={handleContentChange}
                chapterTitle={selectedScene?.title || selectedChapter?.title}
                lineSpacing={lineSpacing}
                quillModules={quillModules}
                chapters={chapters}
                characters={characters}
                locations={locations}
                notes={plotNotes}
            />

            {/* Find & Replace Dialog */}
            <FindReplace
                isOpen={showFindReplace}
                onClose={() => setShowFindReplace(false)}
                chapters={chapters}
                scenes={scenes}
                onUpdateChapter={async (chapterId, updates) => {
                    await chaptersApi.update(chapterId, updates);
                    setChapters(prev => prev.map(c => c.id === chapterId ? { ...c, ...updates } : c));
                    if (selectedChapter?.id === chapterId) {
                        setEditorContent(updates.content);
                    }
                }}
                onUpdateScene={async (sceneId, updates) => {
                    await scenesApi.update(sceneId, updates);
                    setScenes(prev => prev.map(s => s.id === sceneId ? { ...s, ...updates } : s));
                    if (selectedScene?.id === sceneId) {
                        setEditorContent(updates.content);
                    }
                }}
                currentChapterId={selectedChapter?.id}
                currentSceneId={selectedScene?.id}
            />
        </div>
    );
};

const SortableChapterItem = ({ chapter, isSelected, onClick, onDelete }) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: chapter.id });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`p-3 rounded-lg cursor-pointer transition-colors group ${
                isSelected ? 'bg-primary text-primary-foreground' : 'bg-card hover:bg-muted'
            }`}
            onClick={onClick}
            data-testid={`chapter-${chapter.id}`}
        >
            <div className="flex items-center gap-2">
                <button {...attributes} {...listeners} className="cursor-grab opacity-50 hover:opacity-100">
                    <GripVertical className="h-4 w-4" />
                </button>
                <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm line-clamp-1">{chapter.title}</div>
                    <div className="text-xs opacity-70">{chapter.word_count || 0} words</div>
                </div>
                <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" className="h-6 w-6 opacity-0 group-hover:opacity-100">
                            <MoreVertical className="h-3 w-3" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onDelete(); }} className="text-destructive">
                            <Trash2 className="h-4 w-4 mr-2" /> Delete
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
        </div>
    );
};

const NewChapterButton = ({ bookId, chapters, setChapters }) => {
    const [open, setOpen] = useState(false);
    const [title, setTitle] = useState('');

    const handleCreate = async (e) => {
        e.preventDefault();
        try {
            const response = await chaptersApi.create({
                book_id: bookId,
                title,
                order: chapters.length,
            });
            setChapters([...chapters, response.data]);
            setOpen(false);
            setTitle('');
            toast.success('Chapter created');
        } catch (error) {
            toast.error('Failed to create chapter');
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button variant="outline" className="w-full" data-testid="new-chapter-btn">
                    <Plus className="h-4 w-4 mr-2" /> New Chapter
                </Button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>New Chapter</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreate} className="space-y-4 mt-4">
                    <div className="space-y-2">
                        <Label>Chapter Title</Label>
                        <Input
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="Chapter 1: The Beginning"
                            required
                            data-testid="chapter-title-input"
                        />
                    </div>
                    <div className="flex justify-end gap-3">
                        <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                        <Button type="submit" data-testid="create-chapter-submit">Create</Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
};

const NewSceneButton = ({ chapterId, scenes, setScenes }) => {
    const [open, setOpen] = useState(false);
    const [title, setTitle] = useState('');

    const handleCreate = async (e) => {
        e.preventDefault();
        try {
            const response = await scenesApi.create({
                chapter_id: chapterId,
                title,
                order: scenes.length,
            });
            setScenes([...scenes, response.data]);
            setOpen(false);
            setTitle('');
            toast.success('Scene created');
        } catch (error) {
            toast.error('Failed to create scene');
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button variant="outline" className="w-full" data-testid="new-scene-btn">
                    <Plus className="h-4 w-4 mr-2" /> New Scene
                </Button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>New Scene</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreate} className="space-y-4 mt-4">
                    <div className="space-y-2">
                        <Label>Scene Title</Label>
                        <Input
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="Scene title"
                            required
                            data-testid="scene-title-input"
                        />
                    </div>
                    <div className="flex justify-end gap-3">
                        <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                        <Button type="submit" data-testid="create-scene-submit">Create</Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default BookEditor;
